import { useState } from 'react';
import { Button, Input, Label, Tag, TagGroup, TagGroupList, TextField } from '@heroui/react';

import SuggestionsField from './SuggestionsField';

// Επεξεργασία ετικετών, στη θέση του `react-tagsinput` (ασυντήρητο από το 2018,
// με peer dependency έως React 17 και vendor CSS που παρακαμπτόταν από το θέμα).
//
// Η **εμφάνιση και η αφαίρεση** έρχονται από τη βιβλιοθήκη: το `TagGroup` με
// `onRemove` ενεργοποιεί μόνο του το `Tag.RemoveButton`. Εδώ προστίθεται μόνο ό,τι
// δεν καλύπτει, δηλαδή η **προσθήκη**: ένα πεδίο που δέχεται Enter ή κόμμα.
//
// Τρεις αποφάσεις που δεν φαίνονται στη διεπαφή:
//
// 1. Το **κόμμα υποβάλλει**, όχι μόνο το Enter. Οι ετικέτες γράφονται με κόμματα
//    στη φόρμα καταχώρησης, οπότε ο χρήστης φτάνει εδώ με αυτή τη συνήθεια.
// 2. Το `trim` γίνεται **στην υποβολή** και όχι σε κάθε πάτημα πλήκτρου. Ο
//    αντίστροφος τρόπος έσβηνε το κενό πριν προλάβει να γραφτεί η επόμενη λέξη,
//    και το «digital humanities» κατέληγε «digitalhumanities».
// 3. Τα διπλότυπα απορρίπτονται σιωπηλά. Δεν είναι σφάλμα του χρήστη να
//    ξαναγράψει ετικέτα που υπάρχει, και ένα μήνυμα θα ήταν θόρυβος.
//
// **D49 — οι προτάσεις (ΛΑ-11).** Οι ετικέτες που χρησιμοποιούνται ήδη **στη
// συλλογή** προσφέρονται με ένα πάτημα. Η ελεύθερη ετικέτα **δεν καταργείται**: η
// ΛΑ-11 ζητά «ελεύθερες ετικέτες **ή** επιλογή από υπάρχουσες», και το «ή» δεν
// σημαίνει «μόνο». Οι προτάσεις είναι διευκόλυνση, όχι φράχτης — γι' αυτό το πεδίο
// εισαγωγής μένει ακριβώς όπως ήταν και η περιοχή προτάσεων μπαίνει **κάτω** από
// αυτό, ώστε ο δρόμος του πληκτρολογίου να παραμένει ο πρώτος που συναντιέται.
//
// **Ο κατάλογος έρχεται από τον καλούντα**, ποτέ από κλήση εδώ: για τον πόρο είναι
// οι ετικέτες της συλλογής, δηλαδή ακριβώς τα δεδομένα που η κάρτα «Ετικέτες» έχει
// ήδη στη μνήμη της. **Καμία νέα διαδρομή.**

export function TagsField({ label = 'Ετικέτες', value = [], onChange, description, suggestions = [] }) {
    const [draft, setDraft] = useState('');

    function commit(raw) {
        const additions = raw
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
            .filter((tag) => !value.includes(tag));

        if (additions.length > 0) onChange([...value, ...additions]);
        setDraft('');
    }

    function handleKeyDown(event) {
        if (event.key !== 'Enter' && event.key !== ',') return;
        // Το Enter μέσα σε φόρμα υποβάλλει τη φόρμα. Η ετικέτα δεν είναι υποβολή.
        event.preventDefault();
        commit(draft);
    }

    const remove = (keys) => onChange(value.filter((tag) => !keys.has(tag)));

    return (
        <div className="flex flex-col gap-2">
            <TextField value={draft} onChange={setDraft} aria-label={label}>
                <Label>{label}</Label>
                <div className="flex items-end gap-2">
                    <Input
                        placeholder="π.χ. horizon, προθεσμία 2026"
                        onKeyDown={handleKeyDown}
                        className="flex-1"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onPress={() => commit(draft)}
                        isDisabled={draft.trim() === ''}
                    >
                        Προσθήκη
                    </Button>
                </div>
                {description && <span className="text-sm text-muted">{description}</span>}
            </TextField>

            <SuggestionsField
                suggestions={suggestions}
                value={value}
                onAdd={(tag) => commit(tag)}
                label={`${label}: προτάσεις`}
            />

            {value.length > 0 && (
                <TagGroup aria-label={label} onRemove={remove}>
                    <TagGroupList>
                        {value.map((tag) => (
                            <Tag key={tag} id={tag}>
                                {tag}
                                <Tag.RemoveButton aria-label={`Αφαίρεση της ετικέτας ${tag}`} />
                            </Tag>
                        ))}
                    </TagGroupList>
                </TagGroup>
            )}
        </div>
    );
}

export default TagsField;
